import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  Home,
  Palette,
  Settings,
  Shield,
  User,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { PasswordChangeSection } from '@/features/users/components/PasswordChangeSection'
import { UserPreferencesForm } from '@/features/users/components/UserPreferencesForm'
import { UserProfileForm } from '@/features/users/components/UserProfileForm'
import { userService } from '@/features/users/services/userService'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'

export const Route = createFileRoute('/_auth/settings/profile')({
  component: ProfileSettingsPage,
})

function ProfileSettingsPage() {
  const { setBreadcrumbs } = useBreadcrumbs()
  const { isAuthenticated } = useAuthStore()
  const [activeTab, setActiveTab] = useState('profile')

  // Fetch full user profile with all fields (email_verified, preferences, etc.)
  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => userService.getCurrentUser(),
    enabled: isAuthenticated,
    retry: 1,
  })

  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <span className='flex items-center gap-1 text-muted-foreground'>
              <Settings className='w-4 h-4' />
              Settings
            </span>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Profile</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  useEffect(() => {
    setBreadcrumbs(breadcrumbsContent)
  }, [setBreadcrumbs, breadcrumbsContent])

  if (isLoading) {
    return (
      <div className='container mx-auto py-6 max-w-4xl'>
        <div className='space-y-6'>
          <div>
            <Skeleton className='h-8 w-48 mb-2' />
            <Skeleton className='h-4 w-96' />
          </div>
          <Skeleton className='h-10 w-full' />
          <Card>
            <CardContent className='space-y-6 pt-6'>
              <div className='space-y-4'>
                <Skeleton className='h-10 w-full' />
                <Skeleton className='h-10 w-full' />
                <Skeleton className='h-10 w-full' />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='container mx-auto py-6 max-w-4xl'>
        <Alert variant='destructive'>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>
            Failed to load profile information. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!user) {
    return (
      <div className='container mx-auto py-6 max-w-4xl'>
        <Alert>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>
            No user data available. Please log in again.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='space-y-6'>
        {/* Header */}
        <div className='max-w-3xl mx-auto px-6'>
          <h1 className='text-3xl font-bold'>Profile Settings</h1>
          <p className='text-muted-foreground mt-2'>
            Manage your personal information and preferences
          </p>
        </div>

        {/* Tabs */}
        <div className='max-w-3xl mx-auto px-6'>
          <SubtleTabs value={activeTab} onValueChange={setActiveTab}>
            <SubtleTabsList>
              <SubtleTabsTrigger
                value='profile'
                className='flex items-center gap-2'
              >
                <User className='h-4 w-4' />
                Profile
              </SubtleTabsTrigger>
              <SubtleTabsTrigger
                value='preferences'
                className='flex items-center gap-2'
              >
                <Palette className='h-4 w-4' />
                Preferences
              </SubtleTabsTrigger>
              <SubtleTabsTrigger
                value='security'
                className='flex items-center gap-2'
              >
                <Shield className='h-4 w-4' />
                Security
              </SubtleTabsTrigger>
            </SubtleTabsList>

            <SubtleTabsContent value='profile'>
              <UserProfileForm user={user} />
            </SubtleTabsContent>

            <SubtleTabsContent value='preferences'>
              <UserPreferencesForm user={user} />
            </SubtleTabsContent>

            <SubtleTabsContent value='security'>
              <PasswordChangeSection />
            </SubtleTabsContent>
          </SubtleTabs>
        </div>
      </div>
    </div>
  )
}
